<template>
    <div>
        <div
            v-if="isOnboarding"
            ref="onboarding"
            tabindex="0"
            class="absolute inset-0 mb-16 flex h-max flex-col items-center justify-center md:mb-0"
            autofocus
        >
            <transition enter-class="opacity-0" leave-to-class="opacity-0">
                <div
                    class="absolute inset-0 z-10 bg-black bg-opacity-50 px-2 transition-all duration-200"
                ></div>
            </transition>
            <div class="onboarding overflow-y-auto !overflow-x-hidden">
                <div class="right-[-250px] top-0 blur"></div>
                <div class="blur--bottom bottom-0 left-0"></div>

                <div
                    class="z-10 mx-auto mt-0 flex h-full h-max w-full max-w-[930px] flex-col pb-[100px] md:mt-8"
                >
                    <progress-bar :percentage="percentage"></progress-bar>
                    <div class="mt-4 flex w-full justify-between">
                        <p>{{ $t('onboarding.step') }} {{ currentStep }}/4</p>
                        <p class="font-bold">
                            {{ $t(`onboarding.${currentStep}.step.title`) }}
                        </p>
                    </div>

                    <div
                        class="invisible h-0"
                        :class="{
                            '!visible h-auto': currentStep == 4,
                        }"
                    >
                        <img
                            src="@/assets/img/landing/take-controle-with-myday.png"
                            alt="Drawing of being in control and relaxed"
                            class="mx-auto mt-16 w-[250px] max-w-[540px]"
                            loading="lazy"
                        />
                    </div>

                    <div class="mx-auto mb-6 mt-8 w-full max-w-[520px] md:mb-16 md:mt-16">
                        <h1 class="text-base font-bold md:text-2xl">
                            {{
                                $t(`onboarding.${currentStep}.title`, {
                                    name: profile.full_name,
                                })
                            }}
                        </h1>
                        <p v-html="$t(`onboarding.${currentStep}.text`)"></p>
                    </div>
                    <div v-if="currentStep === 1" class="mx-auto w-full md:max-w-[520px]">
                        <div class="flex w-full flex-col">
                            <strong class="mb-4 font-bold">
                                {{ $t(`onboarding.${currentStep}.question.session`) }}
                            </strong>
                            <div
                                class="mb-8 grid grid-cols-1 gap-2 md:grid-cols-2"
                                data-cy="onboarding-sessions"
                            >
                                <button-toggle
                                    v-for="type in voucherTypes"
                                    :id="appendLocale(tags.voucher[type.id - 1])"
                                    :key="type.id"
                                    :text="$t(`onboarding.${currentStep}.session.type_${type.id}`)"
                                    :active="sessionPreference.includes(type)"
                                    @toggle="addType(type)"
                                ></button-toggle>
                            </div>
                            <strong class="mb-4 font-bold">
                                {{ $t(`onboarding.${currentStep}.question.duration`) }}
                            </strong>
                            <div
                                class="grid grid-cols-1 gap-2 md:grid-cols-2"
                                data-cy="onboarding-duration"
                            >
                                <button-toggle
                                    :id="appendLocale(tags.duration[30])"
                                    :text="$t(`onboarding.${currentStep}.session.30`)"
                                    :active="durationPreference.includes('30')"
                                    @toggle="addDuration('30')"
                                ></button-toggle>
                                <button-toggle
                                    :id="appendLocale(tags.duration[45])"
                                    :text="$t(`onboarding.${currentStep}.session.45`)"
                                    :active="durationPreference.includes('45')"
                                    @toggle="addDuration('45')"
                                ></button-toggle>
                                <button-toggle
                                    :id="appendLocale(tags.duration[60])"
                                    :text="$t(`onboarding.${currentStep}.session.60`)"
                                    :active="durationPreference.includes('60')"
                                    @toggle="addDuration('60')"
                                ></button-toggle>
                                <button-toggle
                                    :id="appendLocale(tags.duration[90])"
                                    :text="$t(`onboarding.${currentStep}.session.90`)"
                                    :active="durationPreference.includes('90')"
                                    @toggle="addDuration('90')"
                                ></button-toggle>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="currentStep == 2">
                        <div class="mx-auto flex max-w-[520px] flex-col gap-4">
                            <div class="gap flex flex-col gap-1">
                                <span class="font-bold">
                                    {{ $t('onboarding.2.type.label') }}
                                </span>
                                <InputSelect
                                    v-model="selectedVoucher"
                                    name="voucher_type_id"
                                    :options="voucherTypesDropdown"
                                    :placeholder="$t('professional.class_modal.voucher_type')"
                                    data-cy="onboarding-sessiontype"
                                />
                            </div>
                            <div class="flex flex-col gap-1">
                                <span class="font-bold">{{ $t('onboarding.2.date.label') }}</span>
                                <InputDate v-model="date" name="date" />
                            </div>
                            <div class="gap 1 flex flex-col">
                                <span class="font-bold">{{ $t('onboarding.2.time.label') }}</span>
                                <div class="mb-4 flex items-center">
                                    <div class="pr-4">
                                        {{ $t('common.from') }}
                                    </div>
                                    <div class="flex-auto" data-cy="startTime">
                                        <InputTime
                                            v-model="startTime"
                                            name="start_time"
                                            :date="date"
                                            data-cy="onboarding-starttime"
                                        />
                                    </div>
                                    <div class="px-4">
                                        {{ $t('common.to') }}
                                    </div>
                                    <div class="w-24 flex-auto shrink" data-cy="endTime">
                                        <InputSelect
                                            v-model="endTime"
                                            name="end_time_id"
                                            :class="{ 'input--disabled': startTime === '' }"
                                            :options="endTimes"
                                            placeholder="
                                                HH:mm
                                            "
                                            data-cy="onboarding-endtime"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-col gap-1">
                                <span class="font-bold">{{ $t('onboarding.2.client.label') }}</span>
                                <div class="flex items-center gap-4">
                                    <div>
                                        <Avatar :profile="profile" size="small" />
                                    </div>

                                    <p>
                                        {{
                                            $t('onboarding.2.client.user', {
                                                name: profile.full_name,
                                            })
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="currentStep == 3" class="mx-auto max-w-[520px]">
                        <div>
                            <div
                                class="flex items-center gap-2 rounded-t-lg border border-2 border-primary bg-[rgba(182,209,196,0.3)] px-8 py-4"
                            >
                                <Icon :icon="['far', 'users']" />
                                <p class="font-bold">
                                    {{ $t(`onboarding.1.session.type_${selectedVoucher}`) }}
                                </p>
                            </div>
                            <div
                                class="flex flex-col gap-4 rounded-b-lg border border-2 border-t-0 border-neutral-lighter px-8 py-6"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon :icon="['far', 'clock']" />
                                    <p>
                                        {{ date.format('dddd, Do MMMM') }} |
                                        {{ formattedTime }}
                                    </p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <Icon :icon="['far', 'user']" />
                                    <p>{{ profile.full_name }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-8 flex flex-col gap-2">
                            <strong class="font-bold">{{ $t('onboarding.3.info.title') }}</strong>
                            <div v-for="num in [1, 2, 3]" :key="num">
                                <div class="flex items-center gap-5">
                                    <div
                                        class="flex h-6 w-6 min-w-[24px] items-center justify-center rounded-full border border-2 border-primary text-primary"
                                    >
                                        <Icon :icon="['far', 'check']" />
                                    </div>
                                    <p>{{ $t(`onboarding.3.info.${num}`) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="currentStep == 4" class="mx-auto w-full max-w-[520px]">
                        <div class="flex w-full">
                            <InputText :value="publicCalendarLink" readonly class="mr-2 md:mr-4" />
                            <button
                                :id="appendLocale(tags.copy)"
                                class="btn--bordered whitespace-nowrap border-2 border-neutral-lighter !capitalize"
                                @click="copyLink()"
                            >
                                {{ $t('profile.public.copy_btn') }}
                            </button>
                        </div>
                        <transition enter-class="opacity-0" leave-to-class="opacity-0">
                            <p
                                v-if="showToast"
                                class="text-xs text-success transition-all duration-300"
                            >
                                {{ $t('professional.clientbook.add_modal.link_copied') }}
                            </p>
                        </transition>
                    </div>
                </div>
                <div
                    class="fixed bottom-0 z-[60] mt-4 flex w-full max-w-[930px] flex-grow items-center justify-between bg-white p-4 md:bg-transparent md:p-8"
                >
                    <div v-if="currentStep != 1" class="flex items-center gap-2">
                        <Icon :icon="['far', 'chevron-left']" />
                        <a
                            :id="appendLocale(`step_${currentStep}_back_button`)"
                            class="font-bold"
                            @click="goToPreviousStep"
                        >
                            {{ $t('common.back') }}
                        </a>
                    </div>
                    <button
                        :id="
                            appendLocale(
                                currentStep == 4
                                    ? tags.go_to_calendar
                                    : `step_${currentStep}_next_step_button`,
                            )
                        "
                        class="btn ml-auto mr-0 max-w-xs !px-8 !py-2 md:w-full"
                        :class="{ 'btn--disabled': invalid }"
                        data-cy="onboarding-continue"
                        @click="goToNextStep"
                    >
                        {{
                            currentStep == 4 ? $t('onboarding.finish') : $t('onboarding.next.step')
                        }}
                    </button>
                </div>
            </div>
        </div>
        <a tabindex="0" class="fixed top-0 overflow-hidden" @focus="$refs.onboarding.focus()" />
    </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import InputText from '@/components/input/InputText';
import InputToggle from '@/components/input/InputToggle';
import {
    CALENDAR_EVENTS_FETCH,
    ONBOARDING_SHOW_STATE_SET,
    ONBOARDING_STORAGE_KEY,
    PROFILE_FETCH,
    PROFILE_ONBOARDING_UPDATE,
    REQUEST,
    VALIDATION_CREATE_MESSAGE,
} from '@/constants';
import Avatar from '@/components/general/Avatar.vue';
import ProgressBar from '@/components/general/ProgressBar.vue';
import ButtonToggle from '@/components/input/ButtonToggle.vue';
import InputSelect from '@/components/input/InputSelect';
import InputTime from '@/components/input/InputTime.vue';
import InputDate from '@/components/input/InputDate.vue';
import moment from 'moment-timezone';
import { appendLocale } from '@/languages';

@Component({
    methods: { appendLocale },
    components: {
        InputTime,
        Avatar,
        InputToggle,
        InputText,
        ProgressBar,
        ButtonToggle,
        InputSelect,
        InputDate,
    },
})
export default class Onboarding extends Vue {
    currentStep = 1;
    percentage = (this.currentStep / 4) * 100;
    sessionPreference = [];
    durationPreference = [];
    selectedVoucher = '';
    invalid = true;
    date = moment();
    endTime = '';
    startTime = null;
    showToast = false;

    tags = {
        voucher: [
            'individual_training_button',
            '2_person_training_button',
            'group_training_button',
        ],
        duration: {
            30: '30_minutes_button',
            45: '45_minutes_button',
            60: '60_minutes_button',
            90: '90_minutes_button',
        },
        copy: 'step_4_copy_button',
        go_to_calendar: 'step_4_go_to_calendar_button',
    };

    @Watch('selectedVoucher')
    @Watch('date')
    @Watch('endTime')
    @Watch('startTime')
    onFormChange() {
        this.checkValid();
    }

    mounted() {}

    async goToNextStep() {
        if (this.currentStep == 4) {
            await this.savePreference();
            await this.saveEvent();
            this.finishOnboarding();
        } else {
            this.currentStep = this.currentStep + 1;
            this.percentage = (this.currentStep / 4) * 100;
            this.checkValid();
        }
    }

    goToPreviousStep() {
        this.currentStep = this.currentStep - 1;
        this.percentage = (this.currentStep / 4) * 100;
        this.checkValid();
    }

    checkValid() {
        if (this.currentStep == 1) {
            if (this.durationPreference.length > 0 && this.sessionPreference.length > 0) {
                this.invalid = false;
            } else {
                this.invalid = true;
            }
        } else if (this.currentStep == 2) {
            if (this.startTime && this.endTime && this.date && this.selectedVoucher) {
                this.invalid = false;
            } else {
                this.invalid = true;
            }
        }
    }

    async savePreference() {
        const response = await this.$store.dispatch(REQUEST, {
            method: 'post',
            url: `user-preferences`,
            data: {
                user_id: this.$store.state.profile.user.id,
                session_preferences: this.sessionPreference,
                duration_preferences: this.durationPreference,
            },
        });

        if (response.status === 201) {
            this.$store.dispatch(VALIDATION_CREATE_MESSAGE, {
                type: 'success',
                message: 'onboarding.preferences.create_success',
            });
        }
    }

    async saveEvent() {
        const response = await this.$store.dispatch(REQUEST, {
            method: 'post',
            url: 'events',
            data: {
                start_time: moment(this.startTime)
                    .set({
                        year: this.date.year(),
                        month: this.date.month(),
                        date: this.date.date(),
                        seconds: 0,
                    })
                    .utc(),
                end_time: moment(this.endTime)
                    .set({
                        year: this.date.year(),
                        month: this.date.month(),
                        date: this.date.date(),
                        seconds: 0,
                    })
                    .utc(),
                voucher_type_id: this.selectedVoucher,
                title: this.$t('onboarding.event.name'),
                client_emails: [this.profile.email],
            },
        });
        if (response.status === 200) {
            this.$store.dispatch(VALIDATION_CREATE_MESSAGE, {
                type: 'success',
                message: 'onboarding.event.create_success',
            });
        }
    }

    addDuration(type) {
        if (this.durationPreference.includes(type)) {
            // Remove type
            this.durationPreference = this.durationPreference.filter((item) => item !== type);
            this.endTime = '';
        } else {
            // Add type
            this.durationPreference.push(type);
        }

        this.checkValid();
    }
    addType(type) {
        if (this.sessionPreference.includes(type)) {
            // Remove type
            this.sessionPreference = this.sessionPreference.filter((item) => item !== type);
            this.selectedVoucher = '';
        } else {
            // Add type
            this.sessionPreference.push(type);
        }

        this.checkValid();
    }

    get formattedTime() {
        return (
            moment(this.startTime).format('HH:mm') + ' - ' + moment(this.endTime).format('HH:mm')
        );
    }

    get voucherTypes() {
        return this.$store.state.vouchers.voucherTypes;
    }
    get voucherTypesDropdown() {
        return this.sessionPreference.map((item) => ({
            label: item.label,
            value: item.id,
        }));
    }

    get startTimes() {
        const times = [
            [10, 0],
            [10, 30],
        ];

        return times.map(([h, m]) => {
            const d = new Date();
            d.setHours(h, m, 0, 0);

            const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

            return {
                label,
                value: d.getTime(),
            };
        });
    }

    get endTimes() {
        return this.durationPreference
            .map((item) => {
                const endTime = this.startTime + item * 60 * 1000; // Add minutes as ms

                const date = new Date(endTime);

                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return {
                    label: `${hours}:${minutes} (${item} minutes)`, // Example: "11:30 (60 minutes)"
                    value: endTime,
                };
            })
            .sort((a, b) => a.value - b.value);
    }

    async finishOnboarding() {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, true);
        this.$store.commit(ONBOARDING_SHOW_STATE_SET, false);

        await this.$store.dispatch(PROFILE_ONBOARDING_UPDATE);
        this.$store.dispatch(PROFILE_FETCH);
        this.$store.dispatch(CALENDAR_EVENTS_FETCH);
    }

    copyLink() {
        navigator.clipboard.writeText(this.publicCalendarLink);
        this.showToast = true;

        setTimeout(() => {
            this.showToast = false;
        }, 4000);
    }

    get isOnboarding() {
        return (
            window.localStorage.getItem(ONBOARDING_STORAGE_KEY) !== 'true' &&
            this.profile.onboarded !== 1 &&
            this.$route.fullPath.includes('professional/')
        );
    }

    get profile() {
        return this.$store.state.profile.user?.professional;
    }

    get publicCalendarLink() {
        return `${window.location.origin}/@${this.profile.username}`;
    }
}
</script>

<style lang="postcss" scoped>
.onboarding {
    @apply z-50 mx-4 flex flex-col items-center rounded bg-white p-6 shadow;
    width: 100vw;
    height: 100vh;
}

.blur {
    width: 400px;
    height: 400px;
    border-radius: 500px;
    background: rgba(76, 158, 127, 0.35);
    filter: blur(250px);
    z-index: 0;
    position: absolute;
}

.blur--bottom {
    width: 400px;
    height: 200px; /* half the height */
    border-top-left-radius: 500px;
    border-top-right-radius: 500px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background: rgba(76, 158, 127, 0.35);
    filter: blur(250px);
    z-index: 0;
    position: absolute;
}

.input--disabled {
    @apply pointer-events-none;
    opacity: 0.6;
}
</style>
